(() => {
  'use strict';

  const runtimeKey = '__miniamiFreePatternRuntime';
  if (window[runtimeKey]) return;
  window[runtimeKey] = true;

  const rootSelector = '[data-miniami-free-pattern]';
  const bindings = new WeakMap();
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const supportsSignal = typeof AbortController === 'function';

  const requestFrame = (callback) => (
    typeof window.requestAnimationFrame === 'function'
      ? window.requestAnimationFrame(callback)
      : window.setTimeout(callback, 0)
  );

  const cancelFrame = (frame) => {
    if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(frame);
    else window.clearTimeout(frame);
  };

  const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

  const rootsIn = (container = document) => {
    const roots = [];
    if (container?.matches?.(rootSelector)) roots.push(container);
    container?.querySelectorAll?.(rootSelector).forEach((root) => roots.push(root));
    return roots;
  };

  const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

  const saveDataEnabled = () => (
    connection?.saveData === true
      || document.documentElement.dataset.saveData === 'true'
      || document.documentElement.dataset.miniamiSaveData === 'true'
  );

  const sharedMotionIsStatic = () => (
    document.visibilityState === 'hidden'
    || prefersReducedMotion()
    || document.documentElement.dataset.miniamiMotionForcedStatic === 'true'
    || window.MiniamiMotion?.isForcedStatic?.() === true
  );

  const setMotionState = (root, nearViewport = true) => {
    const rect = root.getBoundingClientRect?.() || { top: 0, bottom: window.innerHeight };
    const nearDocument = rect.bottom > -200 && rect.top < window.innerHeight + 200;
    const saveData = saveDataEnabled();
    const staticState = sharedMotionIsStatic() || saveData || !nearViewport || !nearDocument;
    root.dataset.motionStatic = String(staticState);
    root.dataset.saveData = String(saveData);
  };

  const measureShelf = (shelf) => {
    if (!shelf?.isConnected) return;
    const viewport = shelf.querySelector('.miniami-parallel-free__shelf-viewport');
    const track = shelf.querySelector('.miniami-parallel-free__shelf-track');
    const group = track?.querySelector('.miniami-parallel-free__shelf-group:not([aria-hidden])');
    if (!viewport || !group) return;
    // The cycle moves exactly one group plus the inter-group gap. Hold the
    // visual velocity roughly steady as the catalogue grows or cards fail.
    // Measure cards individually so sparse/display:contents can become a
    // moving rail again after a viewport resize.
    const cards = [...group.querySelectorAll('[data-free-pattern-product-card]')];
    const gap = Number.parseFloat(window.getComputedStyle(track).columnGap) || 0;
    const groupWidth = cards.reduce((width, card) => width + card.getBoundingClientRect().width, 0)
      + Math.max(0, cards.length - 1) * gap;
    if (!groupWidth) return;
    shelf.dataset.freePatternSparse = String(groupWidth <= viewport.clientWidth);
    track.style.setProperty('--free-pattern-drift-duration', `${Math.max(64, Math.ceil(groupWidth / 24))}s`);
  };

  const removeFailedImage = (image) => {
    const surface = image.closest('[data-remove-on-media-error="true"]');
    if (surface) {
      surface.remove();
      return;
    }

    const optional = image.closest('[data-optional-media]');
    const shelf = image.closest('[data-free-pattern-shelf]');
    const photoBand = image.closest('[data-free-pattern-photo-band]');

    const card = image.closest('[data-free-pattern-product-card]');
    if (shelf && card?.dataset.shopifyProductId) {
      const productId = card.dataset.shopifyProductId;
      shelf.querySelectorAll('[data-free-pattern-product-card]').forEach((peer) => {
        if (peer.dataset.shopifyProductId === productId) peer.remove();
      });
      if (!shelf.querySelector('a[data-free-pattern-product-card]')) shelf.remove();
      else measureShelf(shelf);
      return;
    }

    if (optional) optional.remove();
    else {
      image.remove();
      image.closest('[data-image-frame]')?.setAttribute('data-image-error', 'true');
    }

    if (shelf && !shelf.querySelector('a[data-free-pattern-product-card]')) shelf.remove();
    if (photoBand && !photoBand.querySelector('[data-optional-media]')) photoBand.remove();
  };

  const bindImages = (root, controller, cleanups) => {
    const pendingFrames = [];
    root.querySelectorAll('img').forEach((image) => {
      if (image.dataset.freePatternImageBound === 'true') return;
      image.dataset.freePatternImageBound = 'true';
      const onError = () => removeFailedImage(image);
      const listenerOptions = controller ? { once: true, signal: controller.signal } : { once: true };
      image.addEventListener('error', onError, listenerOptions);
      if (!controller) cleanups.push(() => image.removeEventListener('error', onError));
      if (image.complete && image.naturalWidth === 0) {
        const frame = requestFrame(() => removeFailedImage(image));
        pendingFrames.push(frame);
      }
    });
    cleanups.push(() => pendingFrames.forEach(cancelFrame));
  };

  const resetImageBindings = (root) => {
    root.querySelectorAll('img[data-free-pattern-image-bound]').forEach((image) => {
      delete image.dataset.freePatternImageBound;
    });
  };

  const bindShelfMetrics = (root, controller, cleanups) => {
    const shelf = root.querySelector('[data-free-pattern-shelf]');
    if (!shelf) return;
    let frame = 0;
    const update = () => {
      if (frame) cancelFrame(frame);
      frame = requestFrame(() => {
        frame = 0;
        measureShelf(shelf);
      });
    };
    const listenerOptions = controller ? { signal: controller.signal } : undefined;
    window.addEventListener('resize', update, listenerOptions);
    update();
    cleanups.push(() => {
      if (frame) cancelFrame(frame);
      if (!controller) window.removeEventListener('resize', update);
      delete shelf.dataset.freePatternSparse;
    });
  };

  const bindMotion = (root, controller, cleanups) => {
    const state = { nearViewport: true, observer: null };
    const update = () => setMotionState(root, state.nearViewport);
    const Observer = window.IntersectionObserver;

    if (typeof Observer === 'function') {
      state.observer = new Observer(([entry]) => {
        state.nearViewport = entry?.isIntersecting === true;
        update();
      }, { rootMargin: '200px 0px', threshold: 0.01 });
      state.observer.observe(root);
    }

    const listenerOptions = controller ? { signal: controller.signal } : undefined;
    const onPageHide = () => {
      state.nearViewport = false;
      update();
    };
    const onPageShow = () => {
      state.nearViewport = true;
      update();
    };

    document.addEventListener('visibilitychange', update, listenerOptions);
    window.addEventListener('resize', update, listenerOptions);
    window.addEventListener('pagehide', onPageHide, listenerOptions);
    window.addEventListener('pageshow', onPageShow, listenerOptions);
    document.addEventListener('miniami:motion-change', update, listenerOptions);
    update();

    cleanups.push(() => {
      state.observer?.disconnect();
      if (!controller) {
        document.removeEventListener('visibilitychange', update);
        window.removeEventListener('resize', update);
        window.removeEventListener('pagehide', onPageHide);
        window.removeEventListener('pageshow', onPageShow);
        document.removeEventListener('miniami:motion-change', update);
      }
      delete root.dataset.motionStatic;
      delete root.dataset.saveData;
    });
  };

  const bindShelfFocus = (root, controller, cleanups) => {
    const shelf = root.querySelector('[data-free-pattern-shelf]');
    const viewport = shelf?.querySelector('.miniami-parallel-free__shelf-viewport');
    const track = shelf?.querySelector('.miniami-parallel-free__shelf-track');
    if (!shelf || !viewport || !track) return;

    const focusCardSelector = 'a[data-free-pattern-product-card]';
    let focusFrame = 0;

    const clearFocusFrame = () => {
      if (!focusFrame) return;
      cancelFrame(focusFrame);
      focusFrame = 0;
    };

    const cardIsFocusable = (card) => Boolean(
      card
      && card.matches?.(focusCardSelector)
      && shelf.contains(card)
      && !card.closest?.('[aria-hidden="true"]'),
    );

    const scrollFocusedCardIntoView = (card) => {
      const viewportRect = viewport.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const computed = typeof window.getComputedStyle === 'function'
        ? window.getComputedStyle(card)
        : null;
      const outlineWidth = Number.parseFloat(computed?.outlineWidth) || 0;
      const outlineOffset = Number.parseFloat(computed?.outlineOffset) || 0;
      const focusOutlineClearance = Math.max(6, outlineWidth + outlineOffset);
      const minimumLeft = viewportRect.left + focusOutlineClearance;
      const maximumRight = viewportRect.right - focusOutlineClearance;
      let delta = 0;
      if (cardRect.left < minimumLeft) delta = cardRect.left - minimumLeft;
      else if (cardRect.right > maximumRight) delta = cardRect.right - maximumRight;

      if (delta) {
        const maximumScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        viewport.scrollLeft = clamp(viewport.scrollLeft + delta, 0, maximumScrollLeft);
      }

      // Keep the focused card and its outline inside the browser viewport as
      // well as the shelf viewport. This matters when the shelf is settled
      // on the last fractional row of a tall desktop viewport.
      const settledRect = card.getBoundingClientRect();
      const windowViewportClearance = Math.max(8, focusOutlineClearance);
      const minimumWindowTop = windowViewportClearance;
      const maximumWindowBottom = window.innerHeight - windowViewportClearance;
      const windowDelta = settledRect.top < minimumWindowTop
        ? settledRect.top - minimumWindowTop
        : settledRect.bottom > maximumWindowBottom
          ? settledRect.bottom - maximumWindowBottom
          : 0;
      if (windowDelta && typeof window.scrollBy === 'function') window.scrollBy(0, windowDelta);
      const windowSettledRect = card.getBoundingClientRect();
      if (windowSettledRect.left < minimumLeft || windowSettledRect.right > maximumRight) {
        const maximumScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        const correction = windowSettledRect.left < minimumLeft
          ? windowSettledRect.left - minimumLeft
          : windowSettledRect.right - maximumRight;
        viewport.scrollLeft = clamp(viewport.scrollLeft + correction, 0, maximumScrollLeft);
      }
    };

    const onFocusIn = (event) => {
      const card = event.target?.closest?.(focusCardSelector);
      if (!cardIsFocusable(card)) return;
      clearFocusFrame();
      shelf.dataset.freePatternFocus = 'true';
      void track.offsetWidth;
      scrollFocusedCardIntoView(card);
      focusFrame = requestFrame(() => {
        focusFrame = 0;
        if (shelf.dataset.freePatternFocus !== 'true' || !cardIsFocusable(card)) return;
        scrollFocusedCardIntoView(card);
        focusFrame = requestFrame(() => {
          focusFrame = 0;
          if (shelf.dataset.freePatternFocus === 'true' && cardIsFocusable(card)) {
            scrollFocusedCardIntoView(card);
          }
        });
      });
    };

    const onFocusOut = (event) => {
      if (event.relatedTarget && shelf.contains(event.relatedTarget)) return;
      clearFocusFrame();
      shelf.removeAttribute('data-free-pattern-focus');
      viewport.scrollLeft = 0;
    };

    const listenerOptions = controller ? { signal: controller.signal } : undefined;
    shelf.addEventListener('focusin', onFocusIn, listenerOptions);
    shelf.addEventListener('focusout', onFocusOut, listenerOptions);
    cleanups.push(() => {
      clearFocusFrame();
      shelf.removeAttribute('data-free-pattern-focus');
      viewport.scrollLeft = 0;
      if (!controller) {
        shelf.removeEventListener('focusin', onFocusIn);
        shelf.removeEventListener('focusout', onFocusOut);
      }
    });
  };

  const bindAcquisition = (root, cleanups) => {
    const acquisition = root.querySelector('[data-free-pattern-acquisition]');
    const providerRoot = acquisition?.querySelector('[data-native-free-pattern]');
    const status = acquisition?.querySelector('[data-free-pattern-status]');
    if (!providerRoot || !status) return;

    const helper = acquisition.querySelector('[data-free-pattern-provider-helper]');
    const copy = root.querySelector('[data-free-pattern-provider-copy]');
    const unavailableText = status.textContent;
    // Presentation only: stable provider IDs, no control/text/validation or
    // transport replacement. Host variables carry type, colour and pill radii.
    const styleMarker = 'data-miniami-native-form-style';
    const nativeFormStyle = `
      form[data-testid="form"] { gap: 10px !important; }
      input[data-testid="field-first_name"], input[data-testid="field-email"],
      button[data-testid="btn-form-submit"] { height: 48px !important; min-height: 48px !important; box-sizing: border-box !important; }
      button[data-testid="btn-form-submit"] { font-weight: 500 !important; display: flex !important; align-items: center !important; justify-content: center !important; gap: 8px !important; }
      button[data-testid="btn-form-submit"]::before {
        content: ''; width: 15px; height: 15px; flex: none; background: currentColor;
        mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='5.5' width='18' height='13' rx='3'/%3E%3Cpath d='M4.5 7.5l7.5 5.5 7.5-5.5'/%3E%3C/svg%3E") center / contain no-repeat;
      }
    `;
    const applyNativeStyle = (target) => {
      if (!target || target.querySelector(`style[${styleMarker}]`)) return;
      const style = document.createElement('style');
      style.setAttribute(styleMarker, '');
      style.textContent = nativeFormStyle;
      target.append(style);
    };
    const visible = (element) => {
      if (!element?.isConnected || element.disabled || element.hidden) return false;
      const box = element.getBoundingClientRect();
      const style = window.getComputedStyle(element);
      return box.width > 0 && box.height > 0 && style.display !== 'none' && style.visibility === 'visible';
    };
    const setState = (state) => {
      if (acquisition.dataset.providerState === state) return;
      acquisition.dataset.providerState = state;
      const formReady = state === 'ready';
      const providerOwnsResponse = state === 'provider-response';
      status.hidden = formReady || providerOwnsResponse;
      if (!status.hidden) status.textContent = state === 'loading'
        ? 'Checking email sign-up availability…'
        : unavailableText;
      if (helper) helper.hidden = !formReady;
      if (copy) copy.hidden = !formReady && !providerOwnsResponse;
    };

    let stopped = false;
    let wasReady = false;
    let wasSubmitted = false;
    let frame = 0;
    let shadow = null;
    let shadowObserver = null;
    let poll = 0;
    const started = Date.now();
    const onProviderSubmit = () => { wasSubmitted = true; };
    const lightObserver = typeof MutationObserver === 'function'
      ? new MutationObserver(() => scheduleCheck())
      : null;
    const connectShadow = (next) => {
      if (shadow === next) return;
      shadowObserver?.disconnect();
      shadow?.removeEventListener('submit', onProviderSubmit, true);
      shadow = next;
      wasSubmitted = false;
      applyNativeStyle(shadow);
      shadow?.addEventListener('submit', onProviderSubmit, true);
      shadowObserver = next && typeof MutationObserver === 'function'
        ? new MutationObserver(() => scheduleCheck())
        : null;
      shadowObserver?.observe(next, { childList: true, attributes: true, subtree: true });
    };
    const check = () => {
      if (stopped || !root.isConnected) return;
      const embed = providerRoot.querySelector('shopify-forms-embed');
      connectShadow(embed?.shadowRoot || null);
      applyNativeStyle(shadow);
      const form = shadow?.querySelector('form');
      const firstName = form?.querySelector('input#first_name, input[autocomplete="given-name"], input[name="first_name"]');
      const email = form?.querySelector('input#email, input[type="email"], input[inputmode="email"], input[autocomplete="email"], input[name="email"]');
      const submit = form?.querySelector('button[type="submit"], input[type="submit"]');
      if (visible(embed) && visible(form) && visible(firstName) && visible(email) && firstName !== email && visible(submit)) {
        wasReady = true;
        setState('ready');
      } else if (wasReady && wasSubmitted && visible(embed) && shadow && (!form || visible(form))) {
        // After an observed provider submit, leave its response screen alone.
        // An unprompted form removal instead restores the unavailable status.
        setState('provider-response');
      } else if (Date.now() - started >= 6000 || wasReady) {
        setState('unavailable');
      }
    };
    const scheduleCheck = () => {
      if (stopped || frame) return;
      frame = requestFrame(() => { frame = 0; check(); });
    };
    const pollForLateShadow = () => {
      if (stopped) return;
      scheduleCheck();
      // MutationObservers handle later app insertion/removal. This bounded
      // check also catches a shadow root attached after its host was inserted.
      if (Date.now() - started < 20000) poll = window.setTimeout(pollForLateShadow, 500);
    };

    setState('loading');
    lightObserver?.observe(providerRoot, { childList: true, subtree: true });
    check();
    poll = window.setTimeout(pollForLateShadow, 500);
    const timeout = window.setTimeout(check, 6000);
    cleanups.push(() => {
      stopped = true;
      lightObserver?.disconnect();
      shadowObserver?.disconnect();
      shadow?.removeEventListener('submit', onProviderSubmit, true);
      shadow?.querySelector(`style[${styleMarker}]`)?.remove();
      window.clearTimeout(poll);
      window.clearTimeout(timeout);
      if (frame) cancelFrame(frame);
      acquisition.dataset.providerState = 'unavailable';
      status.hidden = false;
      status.textContent = unavailableText;
      if (helper) helper.hidden = true;
      if (copy) copy.hidden = true;
    });
  };

  const updatePickMixOffer = (root) => {
    const band = root.querySelector('[data-free-pattern-picknmix]');
    if (!band) return;
    // The shared emitter preserves legacy shop json_string. The already-owned
    // native parser validates active offer/product/variant/eligible/tier truth.
    // This caller owns only its source presentation and never provider state.
    const raw = band.querySelector('[data-home-aov-config]')?.textContent;
    const config = (raw && window.MiniamiHomeOffers?.parse(raw)) || null;
    const ready = Boolean(config);
    const statusEnabled = ready && band.dataset.freePatternStatusEnabled === 'true';
    band.dataset.offerBasis = ready ? 'native-aov-configured' : 'native-product-only';
    band.dataset.freePatternOfferReady = String(ready);
    const description = band.querySelector('[data-free-pattern-offer-copy]');
    if (description) description.textContent = ready
      ? description.dataset.freePatternOfferCopy
      : 'Pick the patterns you would like to make.';
    const badge = band.querySelector('[data-free-pattern-offer-badge]');
    if (badge) {
      badge.hidden = !ready;
      const text = badge.querySelector('[data-free-pattern-offer-badge-text]');
      if (text) text.textContent = ready ? `UP TO ${config.maxPercent}% OFF` : '';
    }
    const note = band.querySelector('[data-free-pattern-offer-note]');
    if (note) note.hidden = !ready;
    const status = band.querySelector('.miniami-parallel-free__pick-status');
    if (status) status.dataset.pickDiscountStatus = String(statusEnabled);
    band.querySelectorAll('[data-free-pattern-picked]').forEach((line) => {
      const count = Number(line.dataset.freePatternPicked);
      const tier = config?.tiers.filter((entry) => entry.min <= count).at(-1);
      line.textContent = statusEnabled
        ? `${count} picked · ${tier ? `save ${tier.pct}%` : 'add one more to save'}`
        : '';
      line.classList.toggle('miniami-parallel-free__pick-saving', Boolean(statusEnabled && tier));
    });
  };

  const bindRoot = (root) => {
    if (!root || bindings.has(root)) return;
    const controller = supportsSignal ? new AbortController() : null;
    const cleanups = [];
    resetImageBindings(root);
    bindImages(root, controller, cleanups);
    bindAcquisition(root, cleanups);
    bindMotion(root, controller, cleanups);
    bindShelfMetrics(root, controller, cleanups);
    bindShelfFocus(root, controller, cleanups);
    updatePickMixOffer(root);
    bindings.set(root, { controller, cleanups });
  };

  const cleanupRoot = (root) => {
    const binding = bindings.get(root);
    if (!binding) return;
    binding.controller?.abort();
    binding.cleanups.forEach((cleanup) => cleanup());
    resetImageBindings(root);
    bindings.delete(root);
  };

  const updateMotionState = () => rootsIn().forEach((root) => setMotionState(root, true));

  const boot = () => {
    updateMotionState();
    rootsIn().forEach(bindRoot);
    // Deferred dependency scripts have all run by DOMContentLoaded. Re-apply
    // even if the early interactive boot already bound the native form.
    rootsIn().forEach(updatePickMixOffer);
  };

  connection?.addEventListener?.('change', updateMotionState);
  document.addEventListener('DOMContentLoaded', boot, { once: true });
  document.addEventListener('shopify:section:load', (event) => rootsIn(event.target).forEach(bindRoot));
  document.addEventListener('shopify:section:unload', (event) => rootsIn(event.target).forEach(cleanupRoot));
  document.addEventListener('miniami:motion-change', updateMotionState);
  document.addEventListener('visibilitychange', updateMotionState);
  if (document.readyState !== 'loading') boot();
})();
